/**
 * Dependency-free QR Code generator (ISO/IEC 18004), versions 1–40, ECC L/M/Q/H.
 *
 * BROWSER (<script src="qr.js"></script>):
 *   QR.toCanvas(document.querySelector("canvas"), "hello", { scale: 8 });
 *   element.innerHTML = QR.toSVG(QR.encode("hello"));
 */
(function (root) {
  "use strict";

  // ---------- Tables (index 0 unused, index = version) ----------
  const ECC = { L: { ord: 0, fmt: 1 }, M: { ord: 1, fmt: 0 }, Q: { ord: 2, fmt: 3 }, H: { ord: 3, fmt: 2 } };

  const ECC_CODEWORDS_PER_BLOCK = [
    [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
    [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
    [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
    [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  ];

  const NUM_ECC_BLOCKS = [
    [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
    [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
    [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
    [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],
  ];

  const ALNUM = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:";

  // ---------- Helpers ----------
  const getBit = (x, i) => ((x >>> i) & 1) !== 0;

  function appendBits(arr, val, len) {
    for (let i = len - 1; i >= 0; i--) arr.push((val >>> i) & 1);
  }

  function rawDataModules(ver) {
    let r = (16 * ver + 128) * ver + 64;
    if (ver >= 2) {
      const n = Math.floor(ver / 7) + 2;
      r -= (25 * n - 10) * n - 55;
      if (ver >= 7) r -= 36;
    }
    return r;
  }

  function dataCodewords(ver, e) {
    return Math.floor(rawDataModules(ver) / 8) - ECC_CODEWORDS_PER_BLOCK[e][ver] * NUM_ECC_BLOCKS[e][ver];
  }

  function utf8(str) {
    if (typeof TextEncoder !== "undefined") return Array.from(new TextEncoder().encode(str));
    return Array.from(Buffer.from(str, "utf8"));
  }

  // ---------- Segment (auto: numeric / alphanumeric / byte) ----------
  function makeSegment(text) {
    const bits = [];
    if (/^[0-9]*$/.test(text)) {
      for (let i = 0; i < text.length; i += 3) {
        const c = text.substr(i, 3);
        appendBits(bits, parseInt(c, 10), c.length * 3 + 1);
      }
      return { mode: 0x1, cc: [10, 12, 14], count: text.length, bits };
    }
    if ([...text].every((ch) => ALNUM.includes(ch))) {
      let i = 0;
      for (; i + 1 < text.length; i += 2) appendBits(bits, ALNUM.indexOf(text[i]) * 45 + ALNUM.indexOf(text[i + 1]), 11);
      if (i < text.length) appendBits(bits, ALNUM.indexOf(text[i]), 6);
      return { mode: 0x2, cc: [9, 11, 13], count: text.length, bits };
    }
    const bytes = utf8(text);
    bytes.forEach((b) => appendBits(bits, b, 8));
    return { mode: 0x4, cc: [8, 16, 16], count: bytes.length, bits };
  }

  const ccIndex = (ver) => (ver <= 9 ? 0 : ver <= 26 ? 1 : 2);

  // ---------- Reed-Solomon over GF(256), poly 0x11D ----------
  function gfMul(x, y) {
    let z = 0;
    for (let i = 7; i >= 0; i--) {
      z = (z << 1) ^ ((z >>> 7) * 0x11d);
      z ^= ((y >>> i) & 1) * x;
    }
    return z & 0xff;
  }

  function rsDivisor(degree) {
    const res = new Array(degree).fill(0);
    res[degree - 1] = 1;
    let root = 1;
    for (let i = 0; i < degree; i++) {
      for (let j = 0; j < degree; j++) {
        res[j] = gfMul(res[j], root);
        if (j + 1 < degree) res[j] ^= res[j + 1];
      }
      root = gfMul(root, 0x02);
    }
    return res;
  }

  function rsRemainder(data, divisor) {
    const res = new Array(divisor.length).fill(0);
    for (const b of data) {
      const factor = b ^ res.shift();
      res.push(0);
      divisor.forEach((coef, i) => (res[i] ^= gfMul(coef, factor)));
    }
    return res;
  }

  function addEccAndInterleave(data, ver, e) {
    const numBlocks = NUM_ECC_BLOCKS[e][ver];
    const eccLen = ECC_CODEWORDS_PER_BLOCK[e][ver];
    const raw = Math.floor(rawDataModules(ver) / 8);
    const numShort = numBlocks - (raw % numBlocks);
    const shortLen = Math.floor(raw / numBlocks);
    const div = rsDivisor(eccLen);

    const blocks = [];
    for (let i = 0, k = 0; i < numBlocks; i++) {
      const dat = data.slice(k, k + shortLen - eccLen + (i < numShort ? 0 : 1));
      k += dat.length;
      const ecc = rsRemainder(dat, div);
      if (i < numShort) dat.push(0);
      blocks.push(dat.concat(ecc));
    }

    const out = [];
    for (let i = 0; i < blocks[0].length; i++) {
      blocks.forEach((blk, j) => {
        if (i !== shortLen - eccLen || j >= numShort) out.push(blk[i]);
      });
    }
    return out;
  }

  // ---------- Matrix construction ----------
  function alignmentPositions(ver) {
    if (ver === 1) return [];
    const n = Math.floor(ver / 7) + 2;
    const step = ver === 32 ? 26 : Math.ceil((ver * 4 + 4) / (n * 2 - 2)) * 2;
    const res = [6];
    for (let pos = ver * 4 + 17 - 7; res.length < n; pos -= step) res.splice(1, 0, pos);
    return res;
  }

  function buildMatrix(ver, fmtBits, codewords, forcedMask) {
    const size = ver * 4 + 17;
    const mod = Array.from({ length: size }, () => new Array(size).fill(false));
    const fn = Array.from({ length: size }, () => new Array(size).fill(false));
    const setF = (x, y, dark) => { mod[y][x] = dark; fn[y][x] = true; };

    // Timing patterns
    for (let i = 0; i < size; i++) { setF(6, i, i % 2 === 0); setF(i, 6, i % 2 === 0); }

    // Finder patterns + separators
    [[3, 3], [size - 4, 3], [3, size - 4]].forEach(([cx, cy]) => {
      for (let dy = -4; dy <= 4; dy++) for (let dx = -4; dx <= 4; dx++) {
        const d = Math.max(Math.abs(dx), Math.abs(dy)), x = cx + dx, y = cy + dy;
        if (x >= 0 && x < size && y >= 0 && y < size) setF(x, y, d !== 2 && d !== 4);
      }
    });

    // Alignment patterns
    const ap = alignmentPositions(ver), last = ap.length - 1;
    ap.forEach((ay, i) => ap.forEach((ax, j) => {
      if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) return;
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++)
        setF(ax + dx, ay + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }));

    const drawFormat = (mask) => {
      const data = (fmtBits << 3) | mask;
      let rem = data;
      for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
      const bits = ((data << 10) | rem) ^ 0x5412;
      for (let i = 0; i <= 5; i++) setF(8, i, getBit(bits, i));
      setF(8, 7, getBit(bits, 6));
      setF(8, 8, getBit(bits, 7));
      setF(7, 8, getBit(bits, 8));
      for (let i = 9; i < 15; i++) setF(14 - i, 8, getBit(bits, i));
      for (let i = 0; i < 8; i++) setF(size - 1 - i, 8, getBit(bits, i));
      for (let i = 8; i < 15; i++) setF(8, size - 15 + i, getBit(bits, i));
      setF(8, size - 8, true); // dark module
    };
    drawFormat(0); // reserve area

    // Version info (v7+)
    if (ver >= 7) {
      let rem = ver;
      for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
      const bits = (ver << 12) | rem;
      for (let i = 0; i < 18; i++) {
        const a = size - 11 + (i % 3), b = Math.floor(i / 3), bit = getBit(bits, i);
        setF(a, b, bit); setF(b, a, bit);
      }
    }

    // Data in zigzag order
    let i = 0;
    for (let right = size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      for (let vert = 0; vert < size; vert++) for (let j = 0; j < 2; j++) {
        const x = right - j, upward = ((right + 1) & 2) === 0, y = upward ? size - 1 - vert : vert;
        if (!fn[y][x] && i < codewords.length * 8) {
          mod[y][x] = getBit(codewords[i >>> 3], 7 - (i & 7));
          i++;
        }
      }
    }

    const MASKS = [
      (x, y) => (x + y) % 2 === 0,
      (x, y) => y % 2 === 0,
      (x, y) => x % 3 === 0,
      (x, y) => (x + y) % 3 === 0,
      (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
      (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
      (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
      (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
    ];
    const applyMask = (m) => {
      for (let y = 0; y < size; y++) for (let x = 0; x < size; x++)
        if (!fn[y][x] && MASKS[m](x, y)) mod[y][x] = !mod[y][x];
    };

    let mask = forcedMask;
    if (mask === undefined || mask < 0) {
      let best = Infinity;
      for (let m = 0; m < 8; m++) {
        applyMask(m); drawFormat(m);
        const p = penalty(mod, size);
        if (p < best) { best = p; mask = m; }
        applyMask(m); // XOR undo
      }
    }
    applyMask(mask);
    drawFormat(mask);
    return { mod, size, mask };
  }

  function penalty(mod, size) {
    let p = 0, dark = 0;
    const lines = [];
    for (let y = 0; y < size; y++) lines.push(mod[y]);
    for (let x = 0; x < size; x++) lines.push(mod.map((r) => r[x]));

    const FINDER = [1, 0, 1, 1, 1, 0, 1];
    for (const line of lines) {
      // Rule 1: runs of 5+
      let run = 1;
      for (let i = 1; i <= size; i++) {
        if (i < size && line[i] === line[i - 1]) run++;
        else { if (run >= 5) p += 3 + (run - 5); run = 1; }
      }
      // Rule 3: finder-like patterns
      const at = (i) => (i >= 0 && i < size ? line[i] : false);
      for (let i = 0; i + 7 <= size; i++) {
        if (!FINDER.every((v, k) => at(i + k) === !!v)) continue;
        const before = [1, 2, 3, 4].every((k) => !at(i - k));
        const after = [7, 8, 9, 10].every((k) => !at(i + k));
        if (before || after) p += 40;
      }
    }
    // Rule 2: 2x2 blocks; Rule 4: dark balance
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
      if (mod[y][x]) dark++;
      if (x < size - 1 && y < size - 1) {
        const c = mod[y][x];
        if (c === mod[y][x + 1] && c === mod[y + 1][x] && c === mod[y + 1][x + 1]) p += 3;
      }
    }
    const total = size * size;
    p += (Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1) * 10;
    return p;
  }

  // ---------- Public: encode ----------
  function encode(text, opts = {}) {
    const eccName = (opts.ecc || "M").toUpperCase();
    if (!ECC[eccName]) throw new Error(`Invalid ECC level "${opts.ecc}". Use L, M, Q or H.`);
    const e = ECC[eccName].ord;
    const seg = makeSegment(String(text));

    let ver, capacity;
    for (ver = opts.minVersion || 1; ver <= 40; ver++) {
      capacity = dataCodewords(ver, e) * 8;
      const ccBits = seg.cc[ccIndex(ver)];
      if (seg.count < 1 << ccBits && 4 + ccBits + seg.bits.length <= capacity) break;
    }
    if (ver > 40) throw new Error("Data too long for a QR code at this error-correction level.");

    const bits = [];
    appendBits(bits, seg.mode, 4);
    appendBits(bits, seg.count, seg.cc[ccIndex(ver)]);
    bits.push(...seg.bits);
    appendBits(bits, 0, Math.min(4, capacity - bits.length)); // terminator
    appendBits(bits, 0, (8 - (bits.length % 8)) % 8);        // byte align
    for (let pad = 0xec; bits.length < capacity; pad ^= 0xec ^ 0x11) appendBits(bits, pad, 8);

    const data = [];
    for (let i = 0; i < bits.length; i += 8) data.push(parseInt(bits.slice(i, i + 8).join(""), 2));

    const { mod, size, mask } = buildMatrix(ver, ECC[eccName].fmt, addEccAndInterleave(data, ver, e), opts.mask);
    return {
      version: ver, size, ecc: eccName, mask, modules: mod,
      get: (x, y) => x >= 0 && y >= 0 && x < size && y < size && mod[y][x],
    };
  }

  // ---------- Renderers ----------
  function toSVG(qr, { margin = 4, dark = "#000000", light = "#ffffff", size } = {}) {
    const dim = qr.size + margin * 2;
    let path = "";
    for (let y = 0; y < qr.size; y++) for (let x = 0; x < qr.size; x++)
      if (qr.modules[y][x]) path += `M${x + margin} ${y + margin}h1v1h-1z`;
    const wh = size ? ` width="${size}" height="${size}"` : "";
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}"${wh} shape-rendering="crispEdges">` +
      `<rect width="100%" height="100%" fill="${light}"/><path d="${path}" fill="${dark}"/></svg>\n`;
  }

  // Browser canvas
  function toCanvas(canvas, text, { ecc, margin = 4, scale = 8, width, dark = "#000000", light = "#ffffff" } = {}) {
    const qr = typeof text === "string" ? encode(text, { ecc }) : text;
    const s = width ? width / (qr.size + margin * 2) : scale;
    const dim = width || (qr.size + margin * 2) * scale;
    canvas.width = canvas.height = dim;
    canvas.style.width = dim + "px";
    canvas.style.height = dim + "px";
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = light; ctx.fillRect(0, 0, dim, dim);
    ctx.fillStyle = dark;
    for (let y = 0; y < qr.size; y++) for (let x = 0; x < qr.size; x++)
      if (qr.modules[y][x]) ctx.fillRect((x + margin) * s, (y + margin) * s, s, s);
    return qr;
  }

  const QR = { encode, toSVG, toCanvas };

  if (typeof module !== "undefined" && module.exports) module.exports = QR;
  else root.QR = QR;


})(typeof window !== "undefined" ? window : globalThis);
